/**
 * PWA Prompts - User interface prompts for PWA functionality
 * Features: Install prompt, update notification, offline indicator, sync status
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download,
  RefreshCw,
  Wifi,
  WifiOff,
  CloudOff,
  CheckCircle,
  AlertCircle,
  X,
  Smartphone,
  Monitor,
  Zap,
  Database
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePWA } from '@/hooks/usePWA';
import { TouchOptimizedButton } from '@/components/mobile/TouchOptimized';

interface InstallPromptProps {
  onInstall?: () => void;
  onDismiss?: () => void;
  className?: string;
}

interface UpdatePromptProps {
  onUpdate?: () => void;
  onDismiss?: () => void;
  className?: string;
}

interface OfflineIndicatorProps {
  className?: string;
  showDetails?: boolean;
}

interface SyncStatusProps {
  className?: string;
  showProgress?: boolean;
}

// Install Prompt Component
export const InstallPrompt: React.FC<InstallPromptProps> = ({
  onInstall,
  onDismiss,
  className
}) => {
  const { canInstall, install, isStandalone } = usePWA();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  // Auto-dismiss if already installed or not installable
  useEffect(() => {
    if (isStandalone || !canInstall) {
      setIsDismissed(true);
    }
  }, [isStandalone, canInstall]);

  // Check if user has previously dismissed the prompt
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      setIsDismissed(true);
    }
  }, []);

  const handleInstall = async () => {
    try {
      setIsInstalling(true);
      await install();
      onInstall?.();
    } catch (error) {
      console.error('Installation failed:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('pwa-install-dismissed', 'true');
    onDismiss?.();
  };

  if (!canInstall || isDismissed || isStandalone) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={cn(
          "fixed bottom-4 left-4 right-4 z-50",
          "bg-white rounded-2xl shadow-xl border border-gray-200",
          "p-6 max-w-md mx-auto",
          "md:bottom-6 md:left-auto md:right-6 md:max-w-sm",
          className
        )}
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-blue-600" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 mb-1">
              Install App
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Install Custom Design Manager for faster access and offline capabilities.
            </p>
            
            <div className="flex gap-2">
              <TouchOptimizedButton
                size="sm"
                variant="primary"
                onClick={handleInstall}
                loading={isInstalling}
                className="flex-1"
                hapticFeedback="medium"
              >
                <Download className="w-4 h-4 mr-2" />
                Install
              </TouchOptimizedButton>
              
              <TouchOptimizedButton
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                className="px-3"
              >
                <X className="w-4 h-4" />
              </TouchOptimizedButton>
            </div>
          </div>
        </div>

        {/* Benefits list */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              Faster loading
            </div>
            <div className="flex items-center gap-1">
              <CloudOff className="w-3 h-3" />
              Works offline
            </div>
            <div className="flex items-center gap-1">
              <Monitor className="w-3 h-3" />
              Desktop icon
            </div>
            <div className="flex items-center gap-1">
              <Database className="w-3 h-3" />
              Local storage
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Update Prompt Component
export const UpdatePrompt: React.FC<UpdatePromptProps> = ({
  onUpdate,
  onDismiss,
  className
}) => {
  const { needsUpdate, update } = usePWA();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      await update();
      onUpdate?.();
    } catch (error) {
      console.error('Update failed:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  if (!needsUpdate || isDismissed) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={cn(
          "fixed top-4 left-4 right-4 z-50",
          "bg-gradient-to-r from-blue-500 to-indigo-600",
          "text-white rounded-2xl shadow-xl",
          "p-4 max-w-md mx-auto",
          "md:top-6 md:left-auto md:right-6 md:max-w-sm",
          className
        )}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white bg-opacity-20 rounded-xl flex items-center justify-center flex-shrink-0">
            <RefreshCw className="w-5 h-5" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold mb-1">
              Update Available
            </h3>
            <p className="text-sm opacity-90 mb-3">
              A new version is ready with improvements and bug fixes.
            </p>
            
            <div className="flex gap-2">
              <TouchOptimizedButton
                size="sm"
                variant="secondary"
                onClick={handleUpdate}
                loading={isUpdating}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white border-white border-opacity-30"
                hapticFeedback="medium"
              >
                {isUpdating ? 'Updating...' : 'Update Now'}
              </TouchOptimizedButton>
              
              <TouchOptimizedButton
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                className="text-white hover:bg-white hover:bg-opacity-20 px-3"
              >
                Later
              </TouchOptimizedButton>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Offline Indicator Component
export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  className,
  showDetails = false
}) => {
  const { isOffline, connectionQuality, hasOfflineData, networkInfo } = usePWA();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isOffline && connectionQuality !== 'poor') {
    return null;
  }

  const getStatusColor = () => {
    if (isOffline) return 'bg-red-500';
    if (connectionQuality === 'poor') return 'bg-white';
    return 'bg-white';
  };

  const getStatusText = () => {
    if (isOffline) return 'Offline';
    return 'Poor Connection';
  };

  const getStatusIcon = () => {
    if (isOffline) return <WifiOff className="w-4 h-4" />;
    return <Wifi className="w-4 h-4" />;
  };

  return (
    <motion.div
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -50, opacity: 0 }}
      className={cn(
        "fixed top-0 left-0 right-0 z-40",
        getStatusColor(),
        "text-white text-center py-2 px-4",
        "shadow-lg",
        className
      )}
    >
      <div className="flex items-center justify-center gap-2 max-w-md mx-auto">
        {getStatusIcon()}
        <span className="text-sm font-medium">{getStatusText()}</span>
        
        {hasOfflineData && (
          <div className="flex items-center gap-1 ml-2">
            <Database className="w-3 h-3" />
            <span className="text-xs">Data pending sync</span>
          </div>
        )}

        {showDetails && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="ml-2 text-xs underline"
          >
            {isExpanded ? 'Less' : 'Details'}
          </button>
        )}
      </div>

      <AnimatePresence>
        {isExpanded && showDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-2 text-xs opacity-90">
              {networkInfo && (
                <div className="flex justify-center gap-4">
                  <span>Type: {networkInfo.effectiveType || 'Unknown'}</span>
                  {networkInfo.downlink && (
                    <span>Speed: {networkInfo.downlink} Mbps</span>
                  )}
                  {networkInfo.rtt && (
                    <span>Latency: {networkInfo.rtt}ms</span>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Sync Status Component
export const SyncStatus: React.FC<SyncStatusProps> = ({
  className,
  showProgress = true
}) => {
  const { syncStatus, pendingSyncCount, forceSync } = usePWA();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(syncStatus !== 'idle' || pendingSyncCount > 0);
  }, [syncStatus, pendingSyncCount]);

  if (!isVisible) {
    return null;
  }

  const getStatusIcon = () => {
    switch (syncStatus) {
      case 'syncing':
        return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Database className="w-4 h-4 text-blue-500" />;
    }
  };

  const getStatusText = () => {
    switch (syncStatus) {
      case 'syncing':
        return 'Syncing data...';
      case 'completed':
        return 'Data synced';
      case 'failed':
        return 'Sync failed';
      default:
        return `${pendingSyncCount} items to sync`;
    }
  };

  const getStatusColor = () => {
    switch (syncStatus) {
      case 'syncing':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'completed':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'failed':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-white border-gray-300 text-gray-700';
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: 300, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 300, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={cn(
          "fixed bottom-20 right-4 z-40",
          "rounded-xl border-2 shadow-lg",
          "p-3 max-w-xs",
          "md:bottom-6",
          getStatusColor(),
          className
        )}
      >
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="text-sm font-medium flex-1">
            {getStatusText()}
          </span>
          
          {syncStatus === 'failed' && (
            <TouchOptimizedButton
              size="sm"
              variant="ghost"
              onClick={forceSync}
              className="px-2 py-1 text-xs"
            >
              Retry
            </TouchOptimizedButton>
          )}
        </div>

        {/* Auto-hide completed status */}
        {syncStatus === 'completed' && (
          <motion.div
            initial={{ width: '100%' }}
            animate={{ width: '0%' }}
            transition={{ duration: 3, delay: 1 }}
            className="absolute bottom-0 left-0 h-0.5 bg-green-500"
            onAnimationComplete={() => setIsVisible(false)}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
};

// PWA Status Bar (combines all indicators)
export const PWAStatusBar: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={cn("relative", className)}>
      <OfflineIndicator showDetails />
      <UpdatePrompt />
      <InstallPrompt />
      <SyncStatus />
    </div>
  );
};

export default {
  InstallPrompt,
  UpdatePrompt,
  OfflineIndicator,
  SyncStatus,
  PWAStatusBar
};
